<script lang="ts">
/**
 * Drawer card displaying the AI-suggested item for confirmation.
 *
 * @remarks
 * Shown after AI analysis completes. Presents the suggestion with enough
 * detail for a quick accept/reject decision.
 *
 * **Layout:**
 * - Thumbnail (first captured photo) + name/description header
 * - Compact metadata line: qty, type, container on one line
 * - "Not quite right?" progressive disclosure link for correction input
 * - Action buttons: Edit (1/3 width) + Confirm (2/3 width)
 *
 * **Closing behavior:** Dragging the drawer closed dismisses the
 * suggestion and resets the flow. Confirming either creates the item
 * (items mode) or transitions to the container prompt (container mode).
 *
 * **Correction field:** When revealed, shows a text input with a voice
 * button. Phase 2 will submit corrections back to the AI for re-analysis.
 */
export interface CaptureSuggestionCardProps {
  /** Whether the drawer is open (bound to flow.isSuggestion). */
  open: boolean
  /** AI-suggested item name. */
  name: string
  /** AI-suggested description, or null if the AI couldn't determine one. */
  description: string | null
  /** Suggested quantity. Typically 1 unless the AI detects multiples. */
  quantity: number
  /** Suggested item type/category (e.g. "Power Tools"), or null. */
  itemType: string | null
  /** Current container name, shown in the metadata line for context. */
  containerName: string | null
  /** All captured photo data URLs. */
  photos: string[]
  /** Whether the confirm button should show a loading spinner. */
  saving: boolean
}
</script>

<script setup lang="ts">
import { ref, watch } from "vue"

import { ICON } from "@/lib/icons"

const props = defineProps<CaptureSuggestionCardProps>()

defineEmits<{
  /** Fired when the drawer is opened or closed (drag-down to dismiss). */
  "update:open": [value: boolean]
  /** Fired when the user taps "Confirm". Accepts the suggestion. */
  confirm: []
  /** Fired when the user taps "Edit". Opens full item editor (Phase 2). */
  edit: []
  /** Fired when the user submits a correction for re-analysis. */
  refine: [correction: string]
}>()

/** Controls visibility of the correction input. Hidden until user taps "Not quite right?". */
const showCorrection = ref(false)

/** Text the user types to correct the AI suggestion. */
const correction = ref("")

/** Reset correction state when the suggestion changes (e.g. after refine). */
watch(
  () => props.name,
  () => {
    showCorrection.value = false
    correction.value = ""
  },
)
</script>

<template>
  <UDrawer :open="open" @update:open="$emit('update:open', $event)">
    <template #content>
      <div class="flex flex-col gap-4 p-4">
        <!-- Photo strip -->
        <div v-if="photos.length" class="flex items-center gap-2 overflow-x-auto">
          <div
            v-for="(photo, i) in photos"
            :key="i"
            class="bg-accented h-14 w-14 shrink-0 overflow-hidden rounded-lg"
          >
            <img :src="photo" class="h-full w-full object-cover" alt="Captured photo" />
          </div>
        </div>

        <!-- Item header -->
        <div>
          <h3 class="text-highlighted text-base font-semibold">{{ name }}</h3>
          <p v-if="description" class="text-muted mt-1 line-clamp-2 text-xs">
            {{ description }}
          </p>
        </div>

        <!-- Compact metadata line -->
        <div class="text-muted flex items-center gap-1 text-xs">
          <span>Qty: {{ quantity }}</span>
          <template v-if="itemType">
            <span>·</span>
            <span>{{ itemType }}</span>
          </template>
          <template v-if="containerName">
            <span>·</span>
            <UIcon :name="ICON.container" class="text-[10px]" />
            <span class="text-highlighted font-medium">{{ containerName }}</span>
          </template>
        </div>

        <!-- Correction (progressive disclosure) -->
        <button
          v-if="!showCorrection"
          class="text-primary text-left text-xs font-medium"
          @click="showCorrection = true"
        >
          Not quite right?
        </button>
        <div v-else class="flex items-center gap-2">
          <UInput
            v-model="correction"
            placeholder='e.g. "it&apos;s actually a jigsaw, not recip saw"'
            class="flex-1"
            autofocus
            @keydown.enter="correction.trim() && $emit('refine', correction)"
          />
          <UButton
            v-if="correction.trim()"
            icon="i-lucide-send"
            aria-label="Submit correction"
            color="primary"
            variant="solid"
            size="lg"
            square
            @click="$emit('refine', correction)"
          />
        </div>

        <!-- Actions: Confirm (primary) + Edit (secondary) -->
        <div class="flex w-full flex-col gap-2">
          <UButton :loading="saving" block size="xl" @click="$emit('confirm')"> Confirm </UButton>
          <UButton variant="ghost" color="neutral" block size="xl" @click="$emit('edit')">
            Edit
          </UButton>
        </div>
      </div>
    </template>
  </UDrawer>
</template>
